/**
 * Ядро клиента: состояние, синхронизация, шифрование на лету.
 *
 * Сюда стекается всё, что не относится к отрисовке. Компоненты читают сигналы
 * и вызывают функции отсюда; сетевого кода и криптографии в них нет.
 */
import { signal } from '@preact/signals';
import { api, ApiError, setAuthToken } from './api.js';
import {
  createIdentity,
  DEFAULT_PW_MEM,
  DEFAULT_PW_OPS,
  deriveKeys,
  newSalt,
  openChatKey,
  randomChatKey,
  sealChatKey,
  unwrapIdentitySecret,
  wrapIdentitySecret,
} from './crypto.js';
import { fromBase64, toBase64 } from './bytes.js';
import { decryptAvatar, encryptAvatar, prepareImage, toDataUrl } from './avatar.js';
import { decodeBody, encodeBody, previewOf, type ReplyReference } from './message-body.js';
import { associatedData, decryptMessage, encryptMessage, importChatKey } from './message-crypto.js';
import { disablePush } from './push.js';
import { Socket } from './socket.js';
import * as store from './store.js';
import type { PublicUser, ServerEvent, WireChat, WireMessage } from './types.js';

export const session = signal<store.Session | null>(null);
export const chats = signal<store.StoredChat[]>([]);
export const activeChatId = signal<string | null>(null);
export const messages = signal<store.StoredMessage[]>([]);
export const pending = signal<store.OutboxItem[]>([]);
export const connection = signal<'connecting' | 'online' | 'offline'>('offline');
export const syncing = signal(false);

const socket = new Socket();

/** Импортированные ключи AES живут в памяти: importKey на каждое сообщение — заметная трата. */
const keyCache = new Map<string, CryptoKey>();

/** Цепочка, через которую события с сервера проходят по очереди. */
let eventQueue: Promise<void> = Promise.resolve();

// ─── Вход, регистрация, выход ────────────────────────────────────────────────

function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Браузер';
}

export async function register(input: {
  username: string;
  displayName: string;
  password: string;
  inviteCode?: string;
}): Promise<void> {
  const pwSalt = await newSalt();
  const { authKey, wrapKey } = await deriveKeys(
    input.password,
    pwSalt,
    DEFAULT_PW_OPS,
    DEFAULT_PW_MEM,
  );

  const identity = await createIdentity();
  const wrapped = await wrapIdentitySecret(identity.secretKey, wrapKey);

  const result = await api.register({
    username: input.username,
    displayName: input.displayName,
    ...(input.inviteCode ? { inviteCode: input.inviteCode } : {}),
    pwSalt,
    pwOps: DEFAULT_PW_OPS,
    pwMem: DEFAULT_PW_MEM,
    authKey: toBase64(authKey),
    identityPublicKey: toBase64(identity.publicKey),
    wrappedIdentitySecret: wrapped,
    deviceLabel: deviceLabel(),
  });

  await establish({
    token: result.token,
    userId: result.user.id,
    username: result.user.username,
    displayName: result.user.displayName,
    identityPublicKey: result.user.identityPublicKey,
    identitySecret: identity.secretKey,
  });
}

export async function login(username: string, password: string): Promise<void> {
  // Параметры Argon2id лежат на сервере: пароль нужно прогнать ровно так же,
  // как при регистрации, иначе получится другой authKey.
  const params = await api.passwordParams(username);
  const { authKey, wrapKey } = await deriveKeys(password, params.pwSalt, params.pwOps, params.pwMem);

  const result = await api.login(username, toBase64(authKey), deviceLabel());

  const secretKey = await unwrapIdentitySecret(result.user.wrappedIdentitySecret, wrapKey);
  if (!secretKey) {
    // Сервер пароль принял, а приватный ключ не расшифровался. Это не «неверный
    // пароль» — это рассинхрон ключей, и молча его проглатывать нельзя.
    throw new Error(
      'Сервер принял пароль, но приватный ключ не расшифровался. Похоже, учётная запись повреждена.',
    );
  }

  await establish({
    token: result.token,
    userId: result.user.id,
    username: result.user.username,
    displayName: result.user.displayName,
    identityPublicKey: result.user.identityPublicKey,
    identitySecret: secretKey,
  });
}

async function establish(next: store.Session): Promise<void> {
  await store.saveSession(next);
  session.value = next;
  setAuthToken(next.token);
  connect();
  await syncAll();
}

export async function logout(): Promise<void> {
  await api.logout().catch(() => undefined);

  await disablePush().catch(() => undefined);

  socket.close();
  keyCache.clear();
  // Стираем всё: ключи чатов остаются читаемыми, пока лежат в IndexedDB.
  await store.clearEverything();

  setAuthToken(null);
  session.value = null;
  chats.value = [];
  messages.value = [];
  pending.value = [];
  activeChatId.value = null;
}

/** Восстановление сессии при запуске. */
export async function boot(): Promise<void> {
  const saved = await store.loadSession();
  if (!saved) return;

  session.value = saved;
  setAuthToken(saved.token);

  // Сначала показываем то, что уже лежит локально: приложение должно открываться
  // мгновенно и работать без сети.
  chats.value = await store.allChats();
  pending.value = await store.pendingOutbox();
  await loadAvatarCache();

  connect();
  await syncAll().catch(() => undefined);
}

function connect(): void {
  const current = session.value;
  if (!current) return;

  socket.onStateChange = (state) => {
    connection.value = state;
  };
  /**
   * События обрабатываются строго по одному.
   *
   * Почти каждый обработчик делает «прочитать из хранилища — изменить —
   * записать», а IndexedDB асинхронна. Запусти два таких обработчика
   * одновременно — оба прочитают одно и то же состояние, и второй затрёт
   * запись первого. Именно так терялась отметка о прочтении, когда следом
   * приходило событие о доставке.
   */
  socket.onEvent = (event) => {
    eventQueue = eventQueue
      .then(() => handleServerEvent(event))
      .catch((error) => console.error('ошибка обработки события:', error));
  };
  socket.onReconnect = () => {
    void syncAll().catch(() => undefined);
    void flushOutbox();
  };
  socket.connect(current.token);
}

// ─── Ключи чатов ─────────────────────────────────────────────────────────────

async function chatKey(chatId: string, epoch: number): Promise<CryptoKey | null> {
  const cacheId = `${chatId}:${epoch}`;
  const cached = keyCache.get(cacheId);
  if (cached) return cached;

  const raw = await store.getChatKey(chatId, epoch);
  if (!raw) return null;

  const key = await importChatKey(raw);
  keyCache.set(cacheId, key);
  return key;
}

/** Распечатывает и сохраняет все эпохи, которые пришли с сервера. */
async function absorbChatKeys(wire: WireChat): Promise<void> {
  const current = session.value;
  if (!current) return;

  const identity = { publicKey: fromBase64(current.identityPublicKey), secretKey: current.identitySecret };

  for (const entry of wire.keys) {
    if (await store.getChatKey(wire.id, entry.epoch)) continue;

    const raw = await openChatKey(entry.sealedKey, identity);
    if (!raw) {
      console.warn(`не удалось распечатать ключ чата ${wire.id}, эпоха ${entry.epoch}`);
      continue;
    }
    await store.putChatKey(wire.id, entry.epoch, raw);
  }
}

// ─── Приём данных ────────────────────────────────────────────────────────────

async function ingestChat(wire: WireChat): Promise<store.StoredChat> {
  await absorbChatKeys(wire);
  await store.putUsers(wire.members);

  let title: string | null = null;
  if (wire.titleEnc) {
    const key = await chatKey(wire.id, wire.keyEpoch);
    if (key) {
      title = await decryptMessage(
        key,
        JSON.parse(wire.titleEnc) as { nonce: string; ciphertext: string },
        associatedData(wire.id, 'title', wire.keyEpoch),
      );
    }
  }

  const previous = await store.getChat(wire.id);
  const stored: store.StoredChat = {
    ...wire,
    title,
    preview: previous?.preview ?? null,
    previewAt: previous?.previewAt ?? wire.createdAt,
  };

  await store.putChat(stored);
  return stored;
}

async function ingestMessage(wire: WireMessage): Promise<store.StoredMessage> {
  const key = await chatKey(wire.chatId, wire.keyEpoch);

  const plaintext = key
    ? await decryptMessage(
        key,
        { nonce: wire.nonce, ciphertext: wire.ciphertext },
        associatedData(wire.chatId, wire.senderId, wire.keyEpoch),
      )
    : null;

  const stored: store.StoredMessage = { ...wire, plaintext };
  await store.putMessages([stored]);

  // Превью в списке чатов двигаем только вперёд, иначе подгрузка старой
  // истории перепишет его сообщением недельной давности.
  const chat = await store.getChat(wire.chatId);
  if (chat && wire.seq >= chat.lastSeq) {
    await store.putChat({
      ...chat,
      lastSeq: Math.max(chat.lastSeq, wire.seq),
      preview: plaintext === null ? null : previewOf(plaintext),
      previewAt: wire.createdAt,
    });
  }

  // Отметка доставки — вторая галочка у отправителя. Шлём только за чужие
  // сообщения: своё доставлено по определению, и сервер это уже знает.
  if (wire.senderId !== session.value?.userId) {
    reportDelivered(wire.chatId, wire.seq);
  }

  return stored;
}

/**
 * Отметки доставки копятся и уходят одним кадром на чат.
 *
 * При догоне после долгого офлайна прилетают сотни сообщений; слать за каждое
 * отдельную отметку — значит устроить шторм на ровном месте, когда достаточно
 * одного числа: курсор всё равно двигается только вперёд.
 */
const deliveredPending = new Map<string, number>();
let deliveredTimer: ReturnType<typeof setTimeout> | null = null;

function reportDelivered(chatId: string, seq: number): void {
  deliveredPending.set(chatId, Math.max(deliveredPending.get(chatId) ?? 0, seq));

  if (deliveredTimer) return;
  deliveredTimer = setTimeout(() => {
    deliveredTimer = null;
    const batch = [...deliveredPending];
    deliveredPending.clear();

    for (const [chat, seq] of batch) {
      if (!socket.send({ type: 'delivered', chatId: chat, seq })) {
        void api.markDelivered(chat, seq).catch(() => undefined);
      }
    }
  }, 300);
}

async function handleServerEvent(event: ServerEvent): Promise<void> {
  switch (event.type) {
    case 'message': {
      /*
       * Чат мог быть удалён из списка на этом устройстве. Новое сообщение
       * возвращает его — иначе сообщение легло бы в хранилище, а показать
       * его было бы негде: список строится по чатам, а не по сообщениям.
       */
      if (!(await store.getChat(event.message.chatId))) {
        const { chat } = await api.chat(event.message.chatId);
        await ingestChat(chat);
      }

      await ingestMessage(event.message);

      if (event.message.localId) {
        await store.dequeue(event.message.localId);
        pending.value = await store.pendingOutbox();
      }

      if (event.message.chatId === activeChatId.value) {
        messages.value = await store.chatMessages(event.message.chatId);
        // Чат открыт — значит прочитано.
        void markRead(event.message.chatId, event.message.seq);
      }
      chats.value = await store.allChats();
      return;
    }

    case 'message-edited': {
      await ingestMessage(event.message);
      if (event.message.chatId === activeChatId.value) {
        messages.value = await store.chatMessages(event.message.chatId);
      }
      chats.value = await store.allChats();
      return;
    }

    case 'chat': {
      await ingestChat(event.chat);
      chats.value = await store.allChats();
      return;
    }

    case 'read':
    case 'delivered': {
      const chat = await store.getChat(event.chatId);
      if (!chat) return;

      const cursors = chat.cursors.map((cursor) =>
        cursor.userId === event.userId
          ? {
              ...cursor,
              // Курсоры только растут: события могут прийти не по порядку.
              deliveredSeq: Math.max(cursor.deliveredSeq, event.seq),
              readSeq:
                event.type === 'read' ? Math.max(cursor.readSeq, event.seq) : cursor.readSeq,
            }
          : cursor,
      );

      // Прочитано с другого своего устройства — подтягиваем и собственный курсор.
      const own =
        event.type === 'read' && event.userId === session.value?.userId
          ? Math.max(chat.lastReadSeq, event.seq)
          : chat.lastReadSeq;

      await store.putChat({ ...chat, cursors, lastReadSeq: own });
      chats.value = await store.allChats();
      return;
    }

    case 'avatar': {
      if (event.updatedAt === null) {
        await store.deleteAvatar(event.userId);
        avatarVersion.value++;
      } else {
        await fetchAvatar(event.userId).catch(() => undefined);
      }
      return;
    }

    case 'error':
      console.warn('сервер сообщил об ошибке:', event.message);
      return;

    default:
      return;
  }
}

// ─── Синхронизация ───────────────────────────────────────────────────────────

export async function syncAll(): Promise<void> {
  if (!session.value) return;
  syncing.value = true;

  try {
    const { chats: wireChats } = await api.chats();

    for (const wire of wireChats) {
      await ingestChat(wire);

      // Догоняем то, что пришло, пока не было связи.
      const known = await store.highestSeq(wire.id);
      if (wire.lastSeq > known) {
        let cursor = known;
        // Порциями: после долгого офлайна разрыв может быть в сотни сообщений.
        while (cursor < wire.lastSeq) {
          const page = await api.history(wire.id, { after: cursor, limit: 200 });
          if (page.messages.length === 0) break;
          for (const message of page.messages) await ingestMessage(message);
          cursor = page.messages.at(-1)!.seq;
        }
      }
    }

    // Чаты, из которых нас исключили, с сервера больше не приходят.
    const live = new Set(wireChats.map((chat) => chat.id));
    for (const local of await store.allChats()) {
      if (!live.has(local.id)) await store.deleteChat(local.id);
    }

    chats.value = await store.allChats();
    if (activeChatId.value) {
      messages.value = await store.chatMessages(activeChatId.value);
    }

    // Аватарки подтягиваем в фоне: переписка важнее, и ждать картинки,
    // чтобы показать чаты, незачем.
    void syncAvatars().catch(() => undefined);
    void healAvatarKeys().catch(() => undefined);
  } finally {
    syncing.value = false;
  }
}

// ─── Работа с чатами ─────────────────────────────────────────────────────────

export async function openChat(chatId: string): Promise<void> {
  activeChatId.value = chatId;
  messages.value = await store.chatMessages(chatId);
  pending.value = (await store.pendingOutbox()).filter((item) => item.chatId === chatId);

  const chat = await store.getChat(chatId);
  if (chat && chat.lastReadSeq < chat.lastSeq) await markRead(chatId, chat.lastSeq);

  // Адрес используется Service Worker'ом: открытый чат не нужно уведомлять.
  const url = new URL(location.href);
  url.searchParams.set('chat', chatId);
  // Состояние записи сохраняем: без него popstate не поймёт, на какой
  // экран возвращаться, и системный жест «назад» перестанет работать.
  history.replaceState(history.state, '', url);
}

/**
 * Убирает чат из списка — у себя, на всех своих устройствах.
 *
 * У собеседника переписка остаётся: удалять чужие данные мы не беремся.
 * Если он напишет снова, чат вернётся вместе с новым сообщением, и история
 * подтянется с сервера — она никуда не девалась.
 *
 * Локально стираем всё: сообщения и ключи эпох. Ключи забираются заново
 * при следующей синхронизации, поэтому потери здесь нет, а на устройстве
 * не остаётся ничего от чата, который человек решил убрать.
 */
export async function deleteChat(chatId: string): Promise<void> {
  await api.deleteChat(chatId);

  if (activeChatId.value === chatId) closeChat();

  await store.deleteChat(chatId);

  // Ключи лежат ещё и в памяти. Без этого расшифрованный ключ пережил бы
  // удаление до перезагрузки страницы.
  for (const cacheId of [...keyCache.keys()]) {
    if (cacheId.startsWith(`${chatId}:`)) keyCache.delete(cacheId);
  }

  chats.value = await store.allChats();
  pending.value = await store.pendingOutbox();
}

export function closeChat(): void {
  activeChatId.value = null;
  messages.value = [];

  const url = new URL(location.href);
  url.searchParams.delete('chat');
  // Состояние записи сохраняем: без него popstate не поймёт, на какой
  // экран возвращаться, и системный жест «назад» перестанет работать.
  history.replaceState(history.state, '', url);
}

async function markRead(chatId: string, seq: number): Promise<void> {
  const chat = await store.getChat(chatId);
  if (chat && chat.lastReadSeq < seq) {
    await store.putChat({ ...chat, lastReadSeq: seq });
    chats.value = await store.allChats();
  }
  if (!socket.send({ type: 'read', chatId, seq })) {
    await api.markRead(chatId, seq).catch(() => undefined);
  }
}

export async function startDirectChat(username: string): Promise<string> {
  const current = session.value;
  if (!current) throw new Error('нет сессии');

  const { user: other } = await api.lookupUser(username.trim().toLowerCase());
  if (other.id === current.userId) throw new Error('Нельзя завести чат с самим собой');

  const key = await randomChatKey();
  const result = await api.createChat({
    kind: 'direct',
    titleEnc: null,
    keys: [
      { userId: current.userId, sealedKey: await sealChatKey(key, current.identityPublicKey) },
      { userId: other.id, sealedKey: await sealChatKey(key, other.identityPublicKey) },
    ],
  });

  await ingestChat(result.chat);
  chats.value = await store.allChats();
  return result.chat.id;
}

export async function createGroup(title: string, usernames: string[]): Promise<string> {
  const current = session.value;
  if (!current) throw new Error('нет сессии');

  const members: PublicUser[] = [];
  for (const username of usernames) {
    const { user } = await api.lookupUser(username.trim().toLowerCase());
    if (user.id !== current.userId) members.push(user);
  }

  const key = await randomChatKey();
  const keys = [
    { userId: current.userId, sealedKey: await sealChatKey(key, current.identityPublicKey) },
    ...(await Promise.all(
      members.map(async (member) => ({
        userId: member.id,
        sealedKey: await sealChatKey(key, member.identityPublicKey),
      })),
    )),
  ];

  const created = await api.createChat({ kind: 'group', titleEnc: null, keys });

  // Название шифруется тем же ключом чата и отправляется вторым шагом:
  // до создания чата его идентификатор неизвестен, а он входит в связанные
  // данные и защищает название от переноса в другой чат.
  await store.putChatKey(created.chat.id, created.chat.keyEpoch, key);
  keyCache.set(`${created.chat.id}:${created.chat.keyEpoch}`, await importChatKey(key));

  const titled = await api.setTitle(
    created.chat.id,
    await encryptTitle(created.chat.id, created.chat.keyEpoch, title),
  );

  await ingestChat(titled.chat);
  chats.value = await store.allChats();
  return created.chat.id;
}

async function encryptTitle(chatId: string, epoch: number, title: string): Promise<string> {
  const key = await chatKey(chatId, epoch);
  if (!key) throw new Error('нет ключа чата');

  const envelope = await encryptMessage(key, title, associatedData(chatId, 'title', epoch));
  return JSON.stringify(envelope);
}

/**
 * Смена ключа чата. Делается после исключения участника: до неё у него
 * остаётся рабочий ключ и он продолжит читать переписку.
 */
export async function rekeyChat(chatId: string): Promise<void> {
  const chat = await store.getChat(chatId);
  if (!chat) throw new Error('чат не найден');

  const key = await randomChatKey();
  const keys = await Promise.all(
    chat.members.map(async (member) => ({
      userId: member.id,
      sealedKey: await sealChatKey(key, member.identityPublicKey),
    })),
  );

  const result = await api.rekey(chatId, keys);
  await store.putChatKey(chatId, result.chat.keyEpoch, key);
  keyCache.set(`${chatId}:${result.chat.keyEpoch}`, await importChatKey(key));

  // Название было зашифровано прежним ключом, сервер его обнулил — шифруем заново.
  if (chat.title) {
    const titled = await api.setTitle(
      chatId,
      await encryptTitle(chatId, result.chat.keyEpoch, chat.title),
    );
    await ingestChat(titled.chat);
  } else {
    await ingestChat(result.chat);
  }
  chats.value = await store.allChats();
}

export async function removeMember(chatId: string, userId: string): Promise<void> {
  await api.removeMember(chatId, userId);
  await syncAll();
  // Ключ меняем сразу: иначе исключённый продолжит читать всё новое.
  await rekeyChat(chatId).catch((error) => {
    console.error('не удалось сменить ключ после исключения участника:', error);
  });
}

export async function addMember(chatId: string, username: string): Promise<void> {
  const chat = await store.getChat(chatId);
  if (!chat) throw new Error('чат не найден');

  const key = await store.getChatKey(chatId, chat.keyEpoch);
  if (!key) throw new Error('нет ключа текущей эпохи');

  const { user } = await api.lookupUser(username.trim().toLowerCase());
  await api.addMember(chatId, user.id, await sealChatKey(key, user.identityPublicKey));
  await syncAll();
}

// ─── Отправка ────────────────────────────────────────────────────────────────

export async function sendMessage(
  chatId: string,
  text: string,
  replyTo?: ReplyReference,
): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed) return;

  const chat = await store.getChat(chatId);
  if (!chat) throw new Error('чат не найден');

  const key = await chatKey(chatId, chat.keyEpoch);
  if (!key) throw new Error('нет ключа чата — попробуй обновить страницу');

  const current = session.value!;
  // Ссылка на цитату уезжает внутри шифротекста, а не отдельным полем:
  // так сервер не узнаёт даже структуру разговора — кто кому отвечает.
  const body = encodeBody(replyTo ? { text: trimmed, replyTo } : { text: trimmed });

  const envelope = await encryptMessage(
    key,
    body,
    associatedData(chatId, current.userId, chat.keyEpoch),
  );

  const item: store.OutboxItem = {
    localId: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
    chatId,
    keyEpoch: chat.keyEpoch,
    nonce: envelope.nonce,
    ciphertext: envelope.ciphertext,
    plaintext: body,
    createdAt: Date.now(),
    attempts: 0,
  };

  // Кладём в очередь до отправки. Тогда закрытая на полуслове вкладка или
  // пропавшая сеть не теряют написанное — оно уйдёт при следующем подключении.
  await store.enqueue(item);
  pending.value = (await store.pendingOutbox()).filter((row) => row.chatId === activeChatId.value);

  await flushOutbox();
}

let flushing = false;

export async function flushOutbox(): Promise<void> {
  if (flushing || !session.value) return;
  flushing = true;

  try {
    for (const item of await store.pendingOutbox()) {
      const sentOverSocket = socket.send({
        type: 'send',
        chatId: item.chatId,
        keyEpoch: item.keyEpoch,
        nonce: item.nonce,
        ciphertext: item.ciphertext,
        localId: item.localId,
      });

      // По сокету подтверждение придёт событием message — там же элемент
      // и будет снят с очереди.
      if (sentOverSocket) continue;

      try {
        const { message } = await api.send(item.chatId, {
          keyEpoch: item.keyEpoch,
          nonce: item.nonce,
          ciphertext: item.ciphertext,
          localId: item.localId,
        });
        await ingestMessage(message);
        await store.dequeue(item.localId);
      } catch (error) {
        if (error instanceof ApiError && error.code === 'stale_key_epoch') {
          // Ключ сменился, пока сообщение лежало в очереди. Перешифровать
          // нечем — исходный текст остался только здесь, поэтому шифруем заново.
          await reencryptQueued(item);
          continue;
        }
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          // Сервер отказал по существу (нет доступа, чат удалён) — бесконечно
          // повторять бессмысленно.
          console.error('сообщение отклонено сервером, убираю из очереди:', error.code);
          await store.dequeue(item.localId);
          continue;
        }
        await store.bumpAttempts(item.localId);
        break; // сеть недоступна — остальные тоже не уйдут
      }
    }
  } finally {
    flushing = false;
    pending.value = (await store.pendingOutbox()).filter(
      (row) => row.chatId === activeChatId.value,
    );
  }
}

async function reencryptQueued(item: store.OutboxItem): Promise<void> {
  await syncAll();

  const chat = await store.getChat(item.chatId);
  const key = chat ? await chatKey(item.chatId, chat.keyEpoch) : null;
  if (!chat || !key) {
    await store.dequeue(item.localId);
    return;
  }

  const current = session.value!;
  const envelope = await encryptMessage(
    key,
    item.plaintext,
    associatedData(item.chatId, current.userId, chat.keyEpoch),
  );

  await store.enqueue({
    ...item,
    keyEpoch: chat.keyEpoch,
    nonce: envelope.nonce,
    ciphertext: envelope.ciphertext,
    attempts: item.attempts + 1,
  });
}

// ─── Правка сообщений ────────────────────────────────────────────────────────

/**
 * Правка перешифровывает текст ключом ТЕКУЩЕЙ эпохи, а не той, на которой
 * сообщение было отправлено. Иначе после смены ключа исправленный текст
 * не смогли бы прочитать участники, у которых старой эпохи нет.
 */
export async function editMessage(
  chatId: string,
  messageId: string,
  text: string,
): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed) return;

  const chat = await store.getChat(chatId);
  if (!chat) throw new Error('чат не найден');

  const key = await chatKey(chatId, chat.keyEpoch);
  if (!key) throw new Error('нет ключа чата');

  const existing = (await store.chatMessages(chatId)).find((row) => row.id === messageId);
  const replyTo = existing?.plaintext ? decodeBody(existing.plaintext).replyTo : undefined;

  const current = session.value!;
  // Цитату при правке сохраняем: человек исправляет опечатку, а не отвязывает
  // сообщение от того, на что отвечал.
  const body = encodeBody(replyTo ? { text: trimmed, replyTo } : { text: trimmed });

  const envelope = await encryptMessage(
    key,
    body,
    associatedData(chatId, current.userId, chat.keyEpoch),
  );

  const { message } = await api.editMessage(chatId, messageId, {
    keyEpoch: chat.keyEpoch,
    nonce: envelope.nonce,
    ciphertext: envelope.ciphertext,
  });

  await ingestMessage(message);
  if (chatId === activeChatId.value) messages.value = await store.chatMessages(chatId);
  chats.value = await store.allChats();
}

// ─── Аватарки ────────────────────────────────────────────────────────────────

/** Счётчик, по которому перерисовываются компоненты с аватарками. */
export const avatarVersion = signal(0);

/** Расшифрованные аватарки, готовые к показу. Заполняется при загрузке. */
const avatarCache = new Map<string, string>();

export function avatarUrl(userId: string): string | null {
  // avatarVersion читается намеренно: без этого сигнал не подписывает
  // компонент на обновления и новая аватарка не появится до перерисовки.
  void avatarVersion.value;
  return avatarCache.get(userId) ?? null;
}

/** Поднимает кеш аватарок из хранилища — вызывается при запуске. */
async function loadAvatarCache(): Promise<void> {
  for (const avatar of await store.allAvatars()) {
    avatarCache.set(avatar.userId, avatar.dataUrl);
  }
  avatarVersion.value++;
}

async function fetchAvatar(userId: string): Promise<void> {
  const current = session.value;
  if (!current) return;

  const { avatarEnc, sealedKey, updatedAt } = await api.getAvatar(userId);

  const identity = {
    publicKey: fromBase64(current.identityPublicKey),
    secretKey: current.identitySecret,
  };
  const rawKey = await openChatKey(sealedKey, identity);
  if (!rawKey) return; // ключ запечатан не для нас — показывать нечего

  const blob = await decryptAvatar(avatarEnc, rawKey);
  if (!blob) return;

  const dataUrl = await toDataUrl(blob);
  await store.putAvatar({ userId, dataUrl, updatedAt });
  avatarCache.set(userId, dataUrl);
  avatarVersion.value++;
}

/**
 * Догружает аватарки, которые изменились или которых ещё нет.
 *
 * Сравнение идёт по отметке времени из публичного профиля, поэтому лишних
 * запросов не делается: у большинства людей аватарка не меняется годами.
 */
async function syncAvatars(): Promise<void> {
  const cached = new Map((await store.allAvatars()).map((row) => [row.userId, row.updatedAt]));

  for (const user of await store.allUsers()) {
    if (user.avatarUpdatedAt === null) {
      if (cached.has(user.id)) {
        await store.deleteAvatar(user.id);
        avatarCache.delete(user.id);
        avatarVersion.value++;
      }
      continue;
    }
    if (cached.get(user.id) === user.avatarUpdatedAt) continue;

    await fetchAvatar(user.id).catch(() => undefined);
  }
}

/**
 * Допечатывает ключ своей аватарки тем, кто появился в общих чатах позже.
 *
 * Без этого новый собеседник видел бы вместо аватарки заглушку до тех пор,
 * пока владелец не загрузит картинку заново. Здесь это чинится само.
 */
async function healAvatarKeys(): Promise<void> {
  const key = await store.loadOwnAvatarKey();
  if (!key) return;

  const { users } = await api.avatarMissing();
  if (users.length === 0) return;

  await api.addAvatarKeys(
    await Promise.all(
      users.map(async (user) => ({
        userId: user.id,
        sealedKey: await sealChatKey(key, user.identityPublicKey),
      })),
    ),
  );
}

export async function setAvatar(file: File): Promise<void> {
  const current = session.value;
  if (!current) throw new Error('нет сессии');

  const image = await prepareImage(file);
  const { blob, key } = await encryptAvatar(image);

  // Запечатываем ключ себе и всем, с кем есть общий чат. Список приходит
  // с сервера: он знает состав чатов, но не содержимое.
  const { users } = await api.avatarMissing().catch(() => ({ users: [] as PublicUser[] }));
  const recipients = new Map<string, string>([[current.userId, current.identityPublicKey]]);
  for (const user of users) recipients.set(user.id, user.identityPublicKey);
  for (const chat of await store.allChats()) {
    for (const member of chat.members) recipients.set(member.id, member.identityPublicKey);
  }

  const keys = await Promise.all(
    [...recipients].map(async ([userId, publicKey]) => ({
      userId,
      sealedKey: await sealChatKey(key, publicKey),
    })),
  );

  const { updatedAt } = await api.putAvatar(blob, keys);
  await store.saveOwnAvatarKey(key);

  const own = await decryptAvatar(blob, key);
  if (own) {
    const dataUrl = await toDataUrl(own);
    await store.putAvatar({ userId: current.userId, dataUrl, updatedAt });
    avatarCache.set(current.userId, dataUrl);
    avatarVersion.value++;
  }
}

export async function removeAvatar(): Promise<void> {
  const current = session.value;
  if (!current) return;

  await api.deleteAvatar();
  await store.deleteAvatar(current.userId);
  avatarCache.delete(current.userId);
  avatarVersion.value++;
}

// ─── Прочее ──────────────────────────────────────────────────────────────────

/** Состояние доставки своего сообщения — то, что рисуется галочками. */
export type DeliveryState = 'pending' | 'sent' | 'delivered' | 'read';

/**
 * Считает состояние по курсорам остальных участников.
 *
 * В группе учитывается самый отстающий: две галочки означают, что прочитали
 * все, а не кто-то один. Иначе отметка вводила бы в заблуждение ровно там,
 * где она важнее всего.
 */
export function deliveryState(
  chat: store.StoredChat,
  message: store.StoredMessage,
): DeliveryState {
  const currentId = session.value?.userId;
  const others = chat.cursors.filter((cursor) => cursor.userId !== currentId);
  if (others.length === 0) return 'sent';

  const minRead = Math.min(...others.map((cursor) => cursor.readSeq));
  if (minRead >= message.seq) return 'read';

  const minDelivered = Math.min(...others.map((cursor) => cursor.deliveredSeq));
  if (minDelivered >= message.seq) return 'delivered';

  return 'sent';
}

export function chatDisplayName(chat: store.StoredChat): string {
  if (chat.kind === 'group') return chat.title ?? 'Группа без названия';

  const current = session.value;
  const other = chat.members.find((member) => member.id !== current?.userId);
  return other?.displayName ?? 'Чат';
}

export function unreadCount(chat: store.StoredChat): number {
  return Math.max(0, chat.lastSeq - chat.lastReadSeq);
}
